import { apiService } from './api';
import type {
  IndividualDevelopmentPlan,
  IdpQuarterlyReview,
  CreateIdpDto,
  UpdateIdpDto,
  SubmitQuarterlyReviewDto,
  EmployeeWithoutIdp,
  IdpAuditEvent,
} from '../types/idp';
import type { PagedResult } from '../types/pagedResult';

export const idpService = {
  async getMyIdps(): Promise<IndividualDevelopmentPlan[]> {
    const response = await apiService.get<IndividualDevelopmentPlan[]>('/individual-development-plans/my');
    return response.data;
  },

  async getMyIdp(year: number): Promise<IndividualDevelopmentPlan | null> {
    try {
      const response = await apiService.get<IndividualDevelopmentPlan>(`/individual-development-plans/my/${year}`);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  },

  async getIdp(id: string): Promise<IndividualDevelopmentPlan> {
    const response = await apiService.get<IndividualDevelopmentPlan>(`/individual-development-plans/${id}`);
    return response.data;
  },

  async getEmployeeIdp(employeeId: string, year?: number): Promise<IndividualDevelopmentPlan | null> {
    try {
      const params = year ? { year } : {};
      const response = await apiService.get<IndividualDevelopmentPlan>(
        `/individual-development-plans/employee/${employeeId}`,
        { params }
      );
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  },

  async createIdp(dto: CreateIdpDto): Promise<IndividualDevelopmentPlan> {
    const response = await apiService.post<IndividualDevelopmentPlan>('/individual-development-plans', dto);
    return response.data;
  },

  async updateIdp(id: string, dto: UpdateIdpDto): Promise<IndividualDevelopmentPlan> {
    const response = await apiService.put<IndividualDevelopmentPlan>(`/individual-development-plans/${id}`, dto);
    return response.data;
  },

  async submitIdp(id: string): Promise<IndividualDevelopmentPlan> {
    const response = await apiService.post<IndividualDevelopmentPlan>(`/individual-development-plans/${id}/submit`);
    return response.data;
  },

  async approveIdp(id: string): Promise<IndividualDevelopmentPlan> {
    const response = await apiService.post<IndividualDevelopmentPlan>(`/individual-development-plans/${id}/approve`);
    return response.data;
  },

  async getQuarterlyReviews(idpId: string): Promise<IdpQuarterlyReview[]> {
    const response = await apiService.get<IdpQuarterlyReview[]>(`/individual-development-plans/${idpId}/quarterly-reviews`);
    return response.data;
  },

  async getQuarterlyReviewForReview(reviewId: string): Promise<IdpQuarterlyReview | null> {
    try {
      const response = await apiService.get<IdpQuarterlyReview>(
        `/individual-development-plans/review/${reviewId}/quarterly-review`
      );
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  },

  async submitQuarterlyReview(
    idpId: string,
    reviewCycleId: string,
    dto: SubmitQuarterlyReviewDto
  ): Promise<IdpQuarterlyReview> {
    const response = await apiService.post<IdpQuarterlyReview>(
      `/individual-development-plans/${idpId}/quarterly-reviews/${reviewCycleId}`,
      dto
    );
    return response.data;
  },

  async getPendingApprovalsCount(): Promise<number> {
    const response = await apiService.get<number>(
      '/individual-development-plans/pending-approvals/count'
    );
    return response.data;
  },

  async getPendingApprovals(): Promise<IndividualDevelopmentPlan[]> {
    const response = await apiService.get<IndividualDevelopmentPlan[]>(
      '/individual-development-plans/pending-approvals'
    );
    return response.data;
  },

  async getTeamIdps(): Promise<IndividualDevelopmentPlan[]> {
    const response = await apiService.get<IndividualDevelopmentPlan[]>(
      '/individual-development-plans/team'
    );
    return response.data;
  },

  /** All IDPs in the organisation. Administrator and SeniorManager only. */
  async getAllIdps(): Promise<IndividualDevelopmentPlan[]> {
    const response = await apiService.get<IndividualDevelopmentPlan[]>(
      '/individual-development-plans/all'
    );
    return response.data;
  },

  /** Employees who have no IDP for the current year. Administrator and SeniorManager only. */
  async getEmployeesWithoutIdp(): Promise<EmployeeWithoutIdp[]> {
    const response = await apiService.get<EmployeeWithoutIdp[]>(
      '/individual-development-plans/employees-without-idp'
    );
    return response.data;
  },

  async getAuditTrail(idpId: string): Promise<IdpAuditEvent[]> {
    const response = await apiService.get<IdpAuditEvent[]>(
      `/individual-development-plans/${idpId}/audit-trail`
    );
    return response.data;
  },

  async getAuditLog(params: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDirection?: string;
    searchQuery?: string;
    eventTypeFilter?: string;
  }): Promise<PagedResult<IdpAuditEvent>> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);
    if (params.searchQuery) queryParams.append('searchQuery', params.searchQuery);
    if (params.eventTypeFilter) queryParams.append('eventTypeFilter', params.eventTypeFilter);

    const response = await apiService.get<PagedResult<IdpAuditEvent>>(
      `/individual-development-plans/audit-log?${queryParams.toString()}`
    );
    return response.data;
  },
};
